import {useMemo, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import {SHOWED_GUEST_GROUP_MODAL} from 'utils/constants';
import {PATHS} from 'routes/constants';
import {Skeleton} from '@chekinapp/ui';
import {getGuestRowState} from 'utils/guestRoster';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {HighlightedTitle, useModalOncePerSession} from '@chekin/shared';
import {
  ShareRegistrationLinkSheet,
  useShareRegistrationLinkStore,
} from 'components/ModalSheets/ShareRegistrationLink';
import {useGuestGroup, usePath} from 'hooks';
import {useCanEditGuestCount} from 'hooks/useCanEditGuestCount';
import {useCanShareRegistration} from 'hooks/useCanShareRegistration';
import {useRegisterGuestNavigation} from 'hooks/useRegisterGuestNavigation';
import {useRegistrationReturn} from 'hooks/useRegistrationReturn';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useIsSessionModalReady} from 'hooks/useIsSessionModalReady';
import {useReservationSettingsModal} from 'components/ReservationSettingsModal';
import {PageLayout} from 'components/PageLayout';
import {GuestRoster} from 'components/GuestRoster';
import {IncompleteGuestsModal} from './components/IncompleteGuestsModal';
import {IVOnPropertyAlertBoxes} from './components/IVOnPropertyAlertBoxes';
import {MoreGuestsPill} from './components/MoreGuestsPill';
import {RegistrationHeroCard} from './components/RegistrationHeroCard';

function GuestRegistrationView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {data: reservation} = useReservation();
  const {data: summary, isGuestRegistrationOnly} = useSummary();
  const isReady = useIsSessionModalReady();
  const {guestGroup, isLoading, isFetching, isRegistrationComplete} = useGuestGroup();
  const {getFullPath} = usePath();
  const location = useLocation();
  const rosterRef = useRef<HTMLDivElement>(null);
  const {
    isModalOpen: isShareSheetOpen,
    openModal: openShareLinkModal,
    closeModal: closeShareLinkModal,
  } = useShareRegistrationLinkStore();
  const {handleRegisterNewGuest, handleRegisterMinor} = useRegisterGuestNavigation();
  const {isFromTaxes, goBackToTaxes} = useRegistrationReturn();

  const {
    identityVerificationDetails: {enabled: ivEnabled, underageAllowedAge},
  } = useSchema();
  const showMinors = ivEnabled && underageAllowedAge != null;
  const canEditGuestNumber = useCanEditGuestCount();
  const canShareRegistration = useCanShareRegistration();

  const {shouldOpenModal, markAsShown} = useModalOncePerSession({
    id: reservation?.id,
    storageKey: SHOWED_GUEST_GROUP_MODAL,
    isReady,
  });

  const {Modal: WhoIsComingModal, openModal} = useReservationSettingsModal({
    openModalPerSession: shouldOpenModal,
    showFirstTime: isGuestRegistrationOnly,
    onAutoOpenDismiss: markAsShown,
  });

  const totalGuest = guestGroup?.known_number_of_guests ?? 0;
  const {isIVOnPropertyEnabled} = useIVOnProperty();

  const members = useMemo(() => guestGroup?.members ?? [], [guestGroup?.members]);

  const counts = useMemo(
    () =>
      members.reduce(
        (acc, member) => {
          const state = getGuestRowState(member);
          if (state === 'registered') acc.registered += 1;
          if (state === 'verify' || state === 'details') acc.pending += 1;
          return acc;
        },
        {registered: 0, pending: 0},
      ),
    [members],
  );

  const checkInDate = summary?.check_in_date ?? reservation?.check_in_date;
  const dueDate = checkInDate ? dayjs(checkInDate).format('D MMM') : undefined;

  const onBackClick = () => {
    if (isFromTaxes) {
      goBackToTaxes();
      return;
    }
    navigate(getFullPath(PATHS.home), {replace: true});
  };

  const handleCompleteGuestData = () => {
    navigate(location.pathname, {replace: true, state: null});
  };

  return (
    <PageLayout
      busy={isFetching}
      kicker={t('guest_summary')}
      title={<HighlightedTitle i18nKey="who_are_we_registering_now" />}
      mobileTitle={t('guest_summary')}
      onBack={onBackClick}
      backLabel={isFromTaxes ? t('back_to_taxes') : undefined}
      className="guest-register-view pb-12"
    >
      {isLoading ? (
        <div className="flex w-full flex-col gap-2" aria-hidden>
          <Skeleton className="mb-3.5 h-[150px] w-full rounded-[var(--card-radius)]" />
          <Skeleton className="mx-0.5 mb-1 h-[14px] w-[130px] rounded-md" />
          {[0, 1].map(row => (
            <Skeleton
              key={row}
              className="h-[60px] w-full rounded-[var(--card-radius)]"
            />
          ))}
        </div>
      ) : (
        <div className="guest-register-view__content flex w-full flex-col gap-[22px]">
          <RegistrationHeroCard
            registered={counts.registered}
            pending={counts.pending}
            total={totalGuest}
            dueDate={dueDate}
            isComplete={isRegistrationComplete}
            onShare={canShareRegistration ? openShareLinkModal : undefined}
          />
          {isIVOnPropertyEnabled && <IVOnPropertyAlertBoxes />}
          <GuestRoster
            ref={rosterRef}
            onRegisterNew={handleRegisterNewGuest}
            onRegisterMinor={handleRegisterMinor}
            showMinors={showMinors}
            minorsAge={underageAllowedAge ?? undefined}
            onChangeGuestCount={
              canEditGuestNumber && !isRegistrationComplete ? openModal : undefined
            }
          />
          <MoreGuestsPill rosterRef={rosterRef} />
        </div>
      )}

      {WhoIsComingModal}

      <ShareRegistrationLinkSheet open={isShareSheetOpen} onClose={closeShareLinkModal} />

      <IncompleteGuestsModal
        onShareRegistrationLink={openShareLinkModal}
        onCompleteGuestData={handleCompleteGuestData}
        canShareRegistrationLink={canShareRegistration}
      />
    </PageLayout>
  );
}

export {GuestRegistrationView};
